// src/app/core/services/email-change.service.ts

import { Injectable, inject } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import {
  Firestore,
  doc,
  updateDoc,
  serverTimestamp,
} from '@angular/fire/firestore';
import { NotificationService } from '@shared/services/notification.service';
import { FirebaseError } from 'firebase/app';

export interface EmailChangeDetectedEvent {
  uid: string;
  oldEmail?: string | null;
  newEmail: string;
  changedAt: number;
  reason: 'local-event' | 'auth-firestore-mismatch' | 'confirmed-link';
}

/**
 * =====================================================
 * EmailChangeService
 * =====================================================
 *
 * AJUSTE REALIZADO (Item 1.2):
 * Serviço dedicado para alteração de e-mail do usuário,
 * utilizado EXCLUSIVAMENTE no fluxo controlado por token
 * (EmailChange / EmailConfirmation).
 *
 * - Remove a responsabilidade de alteração de e-mail do AuthService
 * - Evita uso direto de updateEmail fora do fluxo validado
 * - Mantém alinhamento com a arquitetura orientada a fluxo
 *
 * IMPORTANTE:
 * - Este serviço NÃO altera o Firebase Auth diretamente
 * - A troca de e-mail no Auth ocorre apenas via Cloud Function
 * - Aqui ocorre apenas a atualização espelhada no Firestore
 */
@Injectable({
  providedIn: 'root',
})
export class EmailChangeService {

  private auth = inject(Auth);
  private firestore = inject(Firestore);
  private notificationService = inject(NotificationService);

  /* =====================================================
   * AJUSTE REALIZADO (Item 2.2):
   * Estado transitório do e-mail alterado pré-confirmação
   * ===================================================== */
  private readonly PENDING_EMAIL_KEY = 'pendingEmailChange';

  private readonly EMAIL_CHANGE_EVENT_KEY = 'movfascia.emailChangeDetected';
  private readonly EMAIL_CHANGE_SIGNIN_ALERT_KEY = 'movfascia.emailChangeSigninAlert';

  // =====================================================
  // ATUALIZA E-MAIL NO FIRESTORE (FLUXO CONTROLADO)
  // =====================================================
  /**
   * Atualiza o e-mail do usuário no Firestore após
   * confirmação bem-sucedida via token.
   *
   * Este método deve ser chamado SOMENTE por componentes
   * envolvidos no fluxo de confirmação de e-mail.
   *
   * @param uid UID do usuário validado pelo token
   * @param newEmail Novo e-mail confirmado
   */
  async updateEmailFromToken(uid: string, newEmail: string): Promise<void> {
    try {
      if (!uid || !newEmail) {
        throw new Error('UID e novo e-mail são obrigatórios');
      }

      // Atualiza apenas o Firestore
      await updateDoc(doc(this.firestore, `users/${uid}`), {
        email: newEmail,
        updatedAt: serverTimestamp(),
      });

      // Limpa e-mail pendente após confirmação bem-sucedida
      this.clearPendingEmail();

      // Feedback ao usuário
      this.notificationService.presentToast(
        'E-mail atualizado com sucesso!',
        'success'
      );
    } catch (error: unknown) {
      const firebaseError = error as FirebaseError;

      this.notificationService.presentToast(
        'Erro ao atualizar e-mail. Tente novamente.',
        'danger'
      );

      throw firebaseError;
    }
  }

  // =====================================================
  // EVENTO LOCAL DE ALTERAÇÃO DE E-MAIL
  // =====================================================
  /**
   * Grava um evento técnico de alteração de e-mail no navegador atual.
   *
   * Uso principal:
   * - sincronizar abas no mesmo navegador/dispositivo;
   * - permitir que sessões antigas detectem a alteração ao voltar ao foco.
   *
   * Observação:
   * localStorage pode falhar em Safari privado, WebView ou app nativo.
   * Por isso, este evento é apenas otimização local; Firestore/Auth
   * continuam sendo a validação robusta.
   */
  saveDetectedEmailChange(event: EmailChangeDetectedEvent): void {
    try {
      localStorage.setItem(
        this.EMAIL_CHANGE_EVENT_KEY,
        JSON.stringify(event)
      );
    } catch {
      // Falha de storage não deve quebrar o fluxo de segurança.
    }
  }

  /**
   * Lê o evento técnico de alteração de e-mail, se existir.
   */
  readDetectedEmailChange(): EmailChangeDetectedEvent | null {
    try {
      const raw = localStorage.getItem(this.EMAIL_CHANGE_EVENT_KEY);
      if (!raw) return null;

      return JSON.parse(raw) as EmailChangeDetectedEvent;
    } catch {
      return null;
    }
  }

  /**
   * Remove o evento técnico após tratamento.
   */
  clearDetectedEmailChange(): void {
    try {
      localStorage.removeItem(this.EMAIL_CHANGE_EVENT_KEY);
    } catch {
      // Ignora falha de storage.
    }
  }

  // =====================================================
  // ALERTA PERSISTENTE PARA SIGNIN
  // =====================================================
  /**
   * Grava o estado que será consumido pelo Signin.
   *
   * Diferença importante:
   * - EMAIL_CHANGE_EVENT_KEY é para detecção técnica;
   * - EMAIL_CHANGE_SIGNIN_ALERT_KEY é para UX após redirecionamento.
   */
  saveSigninEmailChangeAlert(event: EmailChangeDetectedEvent): void {
    try {
      localStorage.setItem(
        this.EMAIL_CHANGE_SIGNIN_ALERT_KEY,
        JSON.stringify(event)
      );
    } catch {
      // Se falhar, o fluxo de segurança ainda continua via signOut.
    }
  }

  /**
   * Lê o alerta persistente de alteração de e-mail para o Signin.
   */
  readSigninEmailChangeAlert(): EmailChangeDetectedEvent | null {
    try {
      const raw = localStorage.getItem(this.EMAIL_CHANGE_SIGNIN_ALERT_KEY);
      if (!raw) return null;

      return JSON.parse(raw) as EmailChangeDetectedEvent;
    } catch {
      return null;
    }
  }

  /**
   * Remove o alerta persistente após o Signin consumir a informação.
   */
  clearSigninEmailChangeAlert(): void {
    try {
      localStorage.removeItem(this.EMAIL_CHANGE_SIGNIN_ALERT_KEY);
    } catch {
      // Ignora falha de storage.
    }
  }

  // =====================================================
  // CONTROLE DE E-MAIL PENDENTE (PRÉ-CONFIRMAÇÃO)
  // =====================================================

  /**
   * Armazena temporariamente o e-mail alterado
   * enquanto a confirmação não ocorre.
   */
  setPendingEmail(email: string): void {
    if (!email) return;

    try {
      sessionStorage.setItem(this.PENDING_EMAIL_KEY, email);
    } catch {
      // sessionStorage pode falhar em alguns ambientes.
    }
  }

  /**
   * Retorna o e-mail alterado pré-confirmação,
   * se existir.
   */
  getPendingEmail(): string | null {
    try {
      return sessionStorage.getItem(this.PENDING_EMAIL_KEY);
    } catch {
      return null;
    }
  }

  /**
   * Remove o e-mail pendente após confirmação
   * ou abandono do fluxo.
   */
  clearPendingEmail(): void {
    try {
      sessionStorage.removeItem(this.PENDING_EMAIL_KEY);
    } catch {
      // Ignora falha de storage.
    }
  }
}
